import { useCallback } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  useTheme,
  type TextFieldProps,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { en } from '../../locale';

export interface NumberFieldProps extends Omit<TextFieldProps, 'type' | 'onChange' | 'value'> {
  value?: number | string;
  onChange?: (value: number | undefined) => void;
  step?: number;
  min?: number;
  max?: number;
  align?: 'left' | 'right';
}

function toNumber(value: number | string | undefined): number | undefined {
  if (value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function clamp(value: number, min?: number, max?: number): number {
  let next = value;
  if (min !== undefined) next = Math.max(min, next);
  if (max !== undefined) next = Math.min(max, next);
  return next;
}

function roundToStep(value: number, step: number): number {
  const decimals = (step.toString().split('.')[1] ?? '').length;
  return Number(value.toFixed(decimals));
}

export default function NumberField({
  value,
  onChange,
  step = 1,
  min,
  max,
  align = 'right',
  disabled = false,
  size = 'small',
  InputProps: muiInputProps,
  inputProps,
  sx,
  ...rest
}: NumberFieldProps) {
  const theme = useTheme();
  const numericValue = toNumber(value);

  const emitChange = useCallback(
    (next: number) => {
      onChange?.(clamp(roundToStep(next, step), min, max));
    },
    [max, min, onChange, step]
  );

  const adjust = (direction: 1 | -1) => {
    const base = numericValue ?? 0;
    emitChange(base + direction * step);
  };

  const stepper = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        alignSelf: 'stretch',
        width: 28,
        borderRight: `1px solid ${theme.palette.divider}`,
        mr: 0.5,
      }}
    >
      <IconButton
        size="small"
        disabled={
          disabled || (max !== undefined && numericValue !== undefined && numericValue >= max)
        }
        aria-label={en.numberField.increase}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => adjust(1)}
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          borderRadius: '4px 4px 0 0',
          color: theme.palette.text.secondary,
          '&:hover': {
            bgcolor: theme.palette.action.hover,
            color: theme.palette.primary.main,
          },
        }}
      >
        <KeyboardArrowUp sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton
        size="small"
        disabled={
          disabled || (min !== undefined && numericValue !== undefined && numericValue <= min)
        }
        aria-label={en.numberField.decrease}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => adjust(-1)}
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          borderRadius: '0 0 4px 4px',
          color: theme.palette.text.secondary,
          '&:hover': {
            bgcolor: theme.palette.action.hover,
            color: theme.palette.primary.main,
          },
        }}
      >
        <KeyboardArrowDown sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );

  return (
    <TextField
      {...rest}
      type="number"
      size={size}
      disabled={disabled}
      value={value ?? ''}
      onChange={(event) => {
        if (event.target.value === '') {
          onChange?.(undefined);
          return;
        }
        const parsed = toNumber(event.target.value);
        if (parsed !== undefined) {
          emitChange(parsed);
        }
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          pl: 0.5,
          alignItems: 'stretch',
        },
        ...sx,
      }}
      InputProps={{
        ...muiInputProps,
        startAdornment: (
          <>
            <InputAdornment
              position="start"
              sx={{ m: 0, maxHeight: 'unset', height: 'auto', alignSelf: 'stretch' }}
            >
              {stepper}
            </InputAdornment>
            {muiInputProps?.startAdornment}
          </>
        ),
      }}
      inputProps={{
        ...inputProps,
        min,
        max,
        step,
        style: {
          textAlign: align,
          ...(inputProps?.style as object | undefined),
        },
      }}
    />
  );
}
